// REVIEW: 未レビュー

import { remarksSchema } from './campaign';
import {
  buildingNameSchema,
  municipalitiesSchema,
  phoneSchema,
  postalCodeSchema,
  prefectureSchema,
  regexSchema,
  lastUpdatedAtSchema,
} from './company';
import {
  fullWidthCharacter,
  halfWidthAlphanumeric,
  nameRomajiRegex,
  nameKanaRegex,
  halfWidthNumber,
} from './regex';

import { isFromBeforeTo } from '@/utils';
import {
  InferType,
  array,
  boolean,
  number,
  object,
  string,
  date,
} from '@/validations/custom-yup';

/** 企業ID */
export const companyIdSchema = string()
  .label('企業ID');

/** 外部店舗ID */
export const externalShopIdSchema = string()
  .label('外部店舗ID')
  .notRequired()
  .max(100)
  .when(
    'regex',
    ([regex], schema) => (regex ? schema.matches(regex, '外部店舗IDのフォーマットを確認してください。') : schema),
  );

/** 店舗名 */
export const shopNameSchema = string()
  .label('店舗名')
  .required()
  .max(100)
  .matches(fullWidthCharacter, '店舗名に使用できない文字が含まれています。');

/** 店舗サイト URL */
export const homepageUrlSchema = string()
  .label('店舗サイト URL')
  .notRequired()
  .url('店舗サイト URLのフォーマットを確認してください。')
  .max(255);

/** 利用開始日 */
export const shopStartDateSchema = date()
  .label('利用開始日')
  .required()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined));

/** 利用終了日 */
export const shopEndDateSchema = date()
  .label('利用終了日')
  .notRequired()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'shopStartDate',
    ([shopStartDate], schema) => (shopStartDate instanceof Date
      ? schema
        .test(
          'isMoreThanStartsAt',
          '利用終了日は利用開始日以降の日付を入力してください。',
          (value) => (value ? value.getTime() >= shopStartDate.getTime() : true),
        )
      : schema),
  );

/** 店舗位置情報/緯度 */
export const latitudeSchema = number()
  .label('緯度')
  .nullable()
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .typeError('緯度は数値で入力してください。')
  .min(-85, '緯度は±85以内で入力してください。')
  .max(85, '緯度は±85以内で入力してください。');

/** 店舗位置情報/経度 */
export const longitudeSchema = number()
  .label('経度')
  .nullable()
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .typeError('経度は数値で入力してください。')
  .min(-180, '経度は±180以内で入力してください。')
  .max(180, '経度は±180以内で入力してください。');

const latlngSchema = {
  latitude: latitudeSchema.when('longitude', ([longitude], schema) => (longitude != null ? schema.required() : schema.notRequired())),
  longitude: longitudeSchema.when('latitude', ([latitude], schema) => (latitude != null ? schema.required() : schema.notRequired())),
};

// 循環参照回避のため [['latitude', 'longitude']] を追加
export const latlngTestSchema = object().shape(latlngSchema, [['latitude', 'longitude']]);

/** 契約店ID */
export const contractStoreIdSchema = string()
  .label('契約店ID')
  .notRequired()
  .test(
    'isLength6',
    '契約店IDは6文字で入力してください。',
    (value) => (value ? value.length === 6 : true),
  );

/** 決済金額上限額/回 */
export const maxSettlementAmountSchema = number()
  .label('決済金額上限額/回')
  .notRequired()
  .min(0, '1回あたりの決済上限額は0~99999999以内で入力してください。')
  .max(99999999, '1回あたりの決済上限額は0~99999999以内で入力してください。');

/** ウォレットサービス決済設定ID */
export const walletServiceSettlementSettingIdSchema = number()
  .label('ウォレットサービス決済設定ID')
  .required();

/** 取扱開始日 */
export const settlementServiceStartsAtSchema = date()
  .label('取扱開始日')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .required();

/** 取扱終了日 */
export const settlementServiceEndsAtSchema = date()
  .label('取扱終了日')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .notRequired()
  .when(
    'settlementServiceStartsAt',
    ([settlementServiceStartsAt], schema) => schema
      .test(
        '終了日時が開始日時以降か',
        '終了日は開始日より後の日付を入力してください。',
        (settlementServiceEndsAt) => settlementServiceEndsAt == null || isFromBeforeTo(settlementServiceStartsAt, settlementServiceEndsAt),
      ),
  );

/** 決済サービス */
export const settlementServiceSchema = string()
  .label('決済サービス');

/** 決済区分 */
export const paymentTypeSchema = string()
  .label('決済区分');

/** 口振口座決済手数料率 */
export const directBankFeeRatioSchema = number()
  .label('口振口座決済手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'BANK_PAY' ? schema.required() : schema.notRequired()))
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** クレジットカード決済手数料率 */
export const creditFeeRatioSchema = number()
  .label('手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'PSP_GMO' || settlementService === 'PSP_DGFT' ? schema.required() : schema.notRequired()))
  .notRequired()
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** プリペイド入金手数料率 */
export const prepaidChargeFeeRatioSchema = number()
  .label('プリペイド入金手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'BALANCE_MANAGER' ? schema.required() : schema.notRequired()))
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** プリペイド利用手数料率 */
export const prepaidSettlementFeeRatioSchema = number()
  .label('プリペイド利用手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'BALANCE_MANAGER' ? schema.required() : schema.notRequired()))
  .notRequired()
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** GMO-PGショップID */
export const pspGmoShopIdSchema = string()
  .label('GMO-PGショップID')
  .when(
    ['settlementService', 'pspGmoCompUsageFlg'],
    (
      [
        settlementService,
        pspGmoCompUsageFlg,
      ],
      schema,
    ) => (
      settlementService === 'PSP_GMO' && pspGmoCompUsageFlg === false ? schema.required() : schema.notRequired()
    ),
  )
  .matches(halfWidthAlphanumeric, 'GMO-PGショップIDは半角英数字（13文字以内）で入力してください。')
  .max(13, 'GMO-PGショップIDは半角英数字（13文字以内）で入力してください。');

/** GMO-PGショップパスワード */
export const pspGmoShopPasswordSchema = string()
  .label('GMO-PGショップパスワード')
  .when(
    ['settlementService', 'pspGmoCompUsageFlg'],
    (
      [
        settlementService,
        pspGmoCompUsageFlg,
      ],
      schema,
    ) => (
      settlementService === 'PSP_GMO' && pspGmoCompUsageFlg === false ? schema.required() : schema.notRequired()
    ),
  )
  .matches(halfWidthAlphanumeric, 'GMO-PGショップパスワードは半角英数字（10文字以内）で入力してください。')
  .max(10, 'GMO-PGショップパスワードは半角英数字（10文字以内）で入力してください。');

/**
 * GMO-PG 包括利用フラグ
 * フラグがnullの場合は利用すると解釈する仕様になっているため、デフォルト値をtrueに設定する
 */
export const pspGmoCompUsageFlgSchema = boolean()
  .label('GMO-PG 包括利用フラグ')
  .notRequired()
  .default(true);

/** ハウス決済手数料率 */
export const housePrepaidFeeRatioSchema = string()
  .label('ハウス決済手数料率')
  .notRequired()
  .test('valid-fee', (value, context) => {
    if (!value) return true;
    if (!/^\d*\.?\d*$/.test(value)) {
      return context.createError({ message: '半角数字以外の文字使用不可' });
    }
    if (!/^\d*(\.\d{0,3})?$/.test(value)) {
      return context.createError({ message: '小数点以下は3桁まで入力可能です。' });
    }
    const num = Number(value);
    if (num < 0 || num > 100) {
      return context.createError({ message: '手数料率は0以上100.000以下の半角数字を入力してください。' });
    }
    return true;
  });

/** 商品券決済手数料率（HOUSE_PREPAID の場合は必須） */
export const digitalVoucherFeeRatioSchema = string()
  .label('商品券決済手数料率')
  .when(['paymentType'], ([paymentType], schema) => (
    paymentType === 'HOUSE_PREPAID'
      ? schema.required('商品券決済手数料を入力してください')
      : schema.notRequired()
  ))
  .test('valid-fee', (value, context) => {
    if (!value) return true;
    if (!/^\d*\.?\d*$/.test(value)) {
      return context.createError({ message: '半角数字で入力してください' });
    }
    if (!/^\d*(\.\d{0,3})?$/.test(value)) {
      return context.createError({ message: '小数点以下は3桁まで入力可能' });
    }
    const num = Number(value);
    if (num < 0 || num > 100) {
      return context.createError({ message: '0 〜 100 の範囲で入力してください' });
    }
    return true;
  });

export const shopSettlementSettingArrayDatumSchema = object({
  walletServiceSettlementSettingId: walletServiceSettlementSettingIdSchema,
  settlementService: settlementServiceSchema,
  paymentType: paymentTypeSchema,
  shopSettlementServiceStartDate: settlementServiceStartsAtSchema,
  shopSettlementServiceEndDate: settlementServiceEndsAtSchema,
  directBankFeeRatio: directBankFeeRatioSchema,
  creditFeeRatio: creditFeeRatioSchema,
  prepaidChargeFeeRatio: prepaidChargeFeeRatioSchema,
  prepaidSettlementFeeRatio: prepaidSettlementFeeRatioSchema,
  pspGmoShopId: pspGmoShopIdSchema,
  pspGmoShopPassword: pspGmoShopPasswordSchema,
  pspGmoCompUsageFlg: pspGmoCompUsageFlgSchema,
  housePrepaidFeeRatio: housePrepaidFeeRatioSchema,
  digitalVoucherFeeRatio: digitalVoucherFeeRatioSchema,
});

export type ShopSettlementSettingArrayDatum = InferType<typeof shopSettlementSettingArrayDatumSchema>;

/** 店舗決済設定 */
export const shopSettlementSettingSchema = array(shopSettlementSettingArrayDatumSchema)
  .label('店舗決済設定')
  .required()
  .default([]);

export const shopNameKanaSchema = string()
  .label('店舗名（カナ）')
  .notRequired()
  .max(64)
  .matches(nameKanaRegex, ({ label }) => `${label}は全角カナ・英数字・一部記号で入力してください。`);

export const shopNameRomajiSchema = string()
  .label('店舗名（ローマ字）')
  .notRequired()
  .max(32)
  .matches(nameRomajiRegex, ({ label }) => `${label}は半角英数字・記号（「"」「,」を除く）で入力してください。`);

export const shopMerchantCategoryCodeSchema = string()
  .label('業種コード')
  .notRequired()
  .max(5)
  .matches(halfWidthNumber, ({ label }) => `${label}は半角数字で入力してください。`);

export const paymentCompletionEmailSchema = string()
  .label('決済完了メール送信アドレス')
  .notRequired()
  .max(255)
  .email('決済完了メール送信アドレスの形式が正しくありません。');

export const shopCustomField1Schema = string()
  .label('店舗自由項目1')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'shopCustomField1Regex',
    ([shopCustomField1Regex], schema) => (shopCustomField1Regex ? schema.matches(shopCustomField1Regex, '店舗自由項目１のフォーマットを確認してください。') : schema),
  );

export const shopCustomField2Schema = string()
  .label('店舗自由項目2')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'shopCustomField2Regex',
    ([shopCustomField2Regex], schema) => (shopCustomField2Regex ? schema.matches(shopCustomField2Regex, '店舗自由項目２のフォーマットを確認してください。') : schema),
  );

export const shopCustomField3Schema = string()
  .label('店舗自由項目3')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'shopCustomField3Regex',
    ([shopCustomField3Regex], schema) => (shopCustomField3Regex ? schema.matches(shopCustomField3Regex, '店舗自由項目３のフォーマットを確認してください。') : schema),
  );

export const shopCustomField4Schema = string()
  .label('店舗自由項目4')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'shopCustomField4Regex',
    ([shopCustomField4Regex], schema) => (shopCustomField4Regex ? schema.matches(shopCustomField4Regex, '店舗自由項目４のフォーマットを確認してください。') : schema),
  );

export const shopCustomField5Schema = string()
  .label('店舗自由項目5')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'shopCustomField5Regex',
    ([shopCustomField5Regex], schema) => (shopCustomField5Regex ? schema.matches(shopCustomField5Regex, '店舗自由項目５のフォーマットを確認してください。') : schema),
  );

export const shopCustomField1RegexSchema = string()
  .label('店舗自由項目１正規表現');

export const shopCustomField2RegexSchema = string()
  .label('店舗自由項目２正規表現');

export const shopCustomField3RegexSchema = string()
  .label('店舗自由項目３正規表現');

export const shopCustomField4RegexSchema = string()
  .label('店舗自由項目４正規表現');

export const shopCustomField5RegexSchema = string()
  .label('店舗自由項目５正規表現');

// export const shopUpdateSmartCodeInfoSchema = object({
//   nameKana: shopNameKanaSchema,
//   nameRomaji: shopNameRomajiSchema,
//   merchantCategoryCode: shopMerchantCategoryCodeSchema,
// });

// export type ShopUpdateSmartCodeInfoSchema = InferType<typeof shopUpdateSmartCodeInfoSchema>;

export const shopEditFormSchema = object()
  .shape({
    companyId: companyIdSchema,
    externalId: externalShopIdSchema,
    name: shopNameSchema,
    phone: phoneSchema,
    postalCode: postalCodeSchema,
    prefecture: prefectureSchema,
    municipalities: municipalitiesSchema,
    buildingName: buildingNameSchema,
    shopStartDate: shopStartDateSchema,
    shopEndDate: shopEndDateSchema,
    contractStoreId: contractStoreIdSchema,
    homepageUrl: homepageUrlSchema,
    maxSettlementAmount: maxSettlementAmountSchema,
    shopSettlementSetting: shopSettlementSettingSchema,
    remarks: remarksSchema,
    regex: regexSchema,
    lastUpdatedAt: lastUpdatedAtSchema,
    nameKana: shopNameKanaSchema,
    nameRomaji: shopNameRomajiSchema,
    merchantCategoryCode: shopMerchantCategoryCodeSchema,
    paymentCompletionEmail: paymentCompletionEmailSchema,
    shopCustomField1: shopCustomField1Schema,
    shopCustomField2: shopCustomField2Schema,
    shopCustomField3: shopCustomField3Schema,
    shopCustomField4: shopCustomField4Schema,
    shopCustomField5: shopCustomField5Schema,
    shopCustomField1Regex: shopCustomField1RegexSchema,
    shopCustomField2Regex: shopCustomField2RegexSchema,
    shopCustomField3Regex: shopCustomField3RegexSchema,
    shopCustomField4Regex: shopCustomField4RegexSchema,
    shopCustomField5Regex: shopCustomField5RegexSchema,

  })
  .shape(
    latlngSchema,
    [['latitude', 'longitude']],
  );

export type ShopEditForm = InferType<typeof shopEditFormSchema>;
